import React, { useState, useEffect } from "react";
import Dropdown from "./Dropdown";
import { formatDate } from "../utility";

interface DateRangeSelectorProps {
  onDateChange: (startDate: string, endDate: string, mode: "quarter" | "month") => void;
  initialYear?: number;
  initialQuarter?: string;
  initialMonthYear?: number;
  initialMonth?: string;
  minYear?: number; // Minimum year for month selector (default: 2024)
  showQuarterSelector?: boolean; // Show Year/Quarter selector (default: true)
  showMonthSelector?: boolean; // Show Year/Month selector (default: true)
  yearLabel?: string;
  quarterLabel?: string;
  monthLabel?: string;
  yearPlaceholder?: string;
  quarterPlaceholder?: string;
  monthPlaceholder?: string;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  onDateChange,
  initialYear,
  initialQuarter = "",
  initialMonthYear,
  initialMonth = "",
  minYear = 2024,
  showQuarterSelector = true,
  showMonthSelector = true,
  yearLabel = "Year",
  quarterLabel = "Quarter",
  monthLabel = "Month",
  yearPlaceholder = "Select Year",
  quarterPlaceholder = "Select Quarter",
  monthPlaceholder = "Select Month",
}) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  // Quarter selector state
  const [selectedYear, setSelectedYear] = useState<number | "">(initialYear || currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState<string>(initialQuarter);

  // Month selector state
  const [selectedMonthYear, setSelectedMonthYear] = useState<number | "">(initialMonthYear || currentYear);
  const [selectedMonth, setSelectedMonth] = useState<string>(initialMonth);

  // Generate years for quarter selector (last 6 years)
  const getQuarterYearOptions = () => {
    return Array.from({ length: 6 }).map((_, i) => {
      const y = currentYear - i;
      return { value: y, label: String(y) };
    });
  };

  // Generate years for month selector (from current year to minYear)
  const getMonthYearOptions = () => {
    const years = [];
    for (let y = currentYear; y >= minYear; y--) {
      years.push({ value: y, label: String(y) });
    }
    return years;
  };

  // Get available months based on selected year
  const getAvailableMonths = () => {
    const months = [
      { value: 1, label: "January" },
      { value: 2, label: "February" },
      { value: 3, label: "March" },
      { value: 4, label: "April" },
      { value: 5, label: "May" },
      { value: 6, label: "June" },
      { value: 7, label: "July" },
      { value: 8, label: "August" },
      { value: 9, label: "September" },
      { value: 10, label: "October" },
      { value: 11, label: "November" },
      { value: 12, label: "December" },
    ];

    // If selected year is current year, filter out future months
    if (Number(selectedMonthYear) === currentYear) {
      return months.filter((m) => m.value <= currentMonth);
    }

    // If selected year is in the future, return empty
    if (Number(selectedMonthYear) > currentYear) {
      return [];
    }

    // For past years, return all months
    return months;
  };

  // Quarter options
  const quarterOptions = [
    { value: "Q1", label: "Q1" },
    { value: "Q2", label: "Q2" },
    { value: "Q3", label: "Q3" },
    { value: "Q4", label: "Q4" },
  ];

  // Get quarter date range
  const getQuarterRange = (year: number, quarter: string) => {
    const q = quarter?.toUpperCase();
    let start = new Date(year, 0, 1);
    let end = new Date(year, 2, 31);
    if (q === "Q1") {
      start = new Date(year, 0, 1);
      end = new Date(year, 2, 31);
    } else if (q === "Q2") {
      start = new Date(year, 3, 1);
      end = new Date(year, 5, 30);
    } else if (q === "Q3") {
      start = new Date(year, 6, 1);
      end = new Date(year, 8, 30);
    } else if (q === "Q4") {
      start = new Date(year, 9, 1);
      end = new Date(year, 11, 31);
    }
    return { start, end };
  };

  // Get month date range
  const getMonthRange = (year: number, month: number) => {
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0); // Last day of the month
    return { start, end };
  };

  // Apply year/quarter selection
  const applyYearQuarter = (yearVal: number, quarterVal: string) => {
    if (!yearVal || !quarterVal) return;
    const { start, end } = getQuarterRange(yearVal, quarterVal);
    const newStartStr = formatDate(start);
    const newEndStr = formatDate(end);
    onDateChange(newStartStr, newEndStr, "quarter");
  };

  // Apply year/month selection
  const applyYearMonth = (yearVal: number, monthVal: number) => {
    if (!yearVal || !monthVal) return;
    const { start, end } = getMonthRange(yearVal, monthVal);
    const newStartStr = formatDate(start);
    const newEndStr = formatDate(end);
    onDateChange(newStartStr, newEndStr, "month");
  };

  // Handlers for quarter selector
  const handleYearDropdownChange = (value: string | number) => {
    const yearNum = Number(value);
    setSelectedYear(yearNum);
    if (selectedQuarter) {
      applyYearQuarter(yearNum, selectedQuarter);
    }
  };

  const handleQuarterDropdownChange = (value: string | number) => {
    const quarterStr = String(value);
    setSelectedQuarter(quarterStr);
    if (selectedYear) {
      applyYearQuarter(Number(selectedYear), quarterStr);
    }
  };

  // Handlers for month selector
  const handleMonthYearDropdownChange = (value: string | number) => {
    const yearNum = Number(value);
    setSelectedMonthYear(yearNum);
    // Clear month selection if year changes and current month is invalid for new year
    if (selectedMonth) {
      const monthNum = Number(selectedMonth);
      // If selected year is current year and selected month is in the future, clear month
      if (yearNum === currentYear && monthNum > currentMonth) {
        setSelectedMonth("");
      } else {
        applyYearMonth(yearNum, monthNum);
      }
    }
  };

  const handleMonthDropdownChange = (value: string | number) => {
    const monthNum = Number(value);
    setSelectedMonth(String(monthNum));
    if (selectedMonthYear) {
      applyYearMonth(Number(selectedMonthYear), monthNum);
    }
  };

  // Reset function to clear selections
  const reset = () => {
    setSelectedYear(currentYear);
    setSelectedQuarter("");
    setSelectedMonthYear(currentYear);
    setSelectedMonth("");
  };

  // Expose reset function via ref if needed
  useEffect(() => {
    // This effect can be used to sync with external state if needed
  }, []);

  return (
    <div className="date-range-selector space-y-2">
      {showQuarterSelector && (
        <div className="flex gap-4 justify-between w-full">
          <div className="w-full">
            <label className="block text-10 text-gray-700 mb-1">{yearLabel}</label>
            <Dropdown
              label=""
              options={getQuarterYearOptions()}
              selectedValue={selectedYear}
              onChange={handleYearDropdownChange}
              placeholder={yearPlaceholder}
            />
          </div>
          <div className="w-full">
            <label className="block text-10 text-gray-700 mb-1">{quarterLabel}</label>
            <Dropdown
              label=""
              options={quarterOptions}
              selectedValue={selectedQuarter}
              onChange={handleQuarterDropdownChange}
              placeholder={quarterPlaceholder}
            />
          </div>
        </div>
      )}

      {showMonthSelector && (
        <div className="flex gap-4 justify-between w-full">
          <div className="w-full">
            <label className="block text-10 text-gray-700 mb-1">{yearLabel}</label>
            <Dropdown
              label=""
              options={getMonthYearOptions()}
              selectedValue={selectedMonthYear}
              onChange={handleMonthYearDropdownChange}
              placeholder={yearPlaceholder}
            />
          </div>
          <div className="w-full">
            <label className="block text-10 text-gray-700 mb-1">{monthLabel}</label>
            <Dropdown
              label=""
              options={getAvailableMonths()}
              selectedValue={selectedMonth ? Number(selectedMonth) : ""}
              onChange={handleMonthDropdownChange}
              placeholder={monthPlaceholder}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;

// Export a hook version for more flexibility
export const useDateRangeSelector = (minYear: number = 2024) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState<number | "">(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState<string>("");
  const [selectedMonthYear, setSelectedMonthYear] = useState<number | "">(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<string>("");

  const getQuarterRange = (year: number, quarter: string) => {
    const q = quarter?.toUpperCase();
    let start = new Date(year, 0, 1);
    let end = new Date(year, 2, 31);
    if (q === "Q1") {
      start = new Date(year, 0, 1);
      end = new Date(year, 2, 31);
    } else if (q === "Q2") {
      start = new Date(year, 3, 1);
      end = new Date(year, 5, 30);
    } else if (q === "Q3") {
      start = new Date(year, 6, 1);
      end = new Date(year, 8, 30);
    } else if (q === "Q4") {
      start = new Date(year, 9, 1);
      end = new Date(year, 11, 31);
    }
    return { start, end };
  };

  const getMonthRange = (year: number, month: number) => {
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);
    return { start, end };
  };

  const getQuarterYearOptions = () => {
    return Array.from({ length: 6 }).map((_, i) => {
      const y = currentYear - i;
      return { value: y, label: String(y) };
    });
  };

  const getMonthYearOptions = () => {
    const years = [];
    for (let y = currentYear; y >= minYear; y--) {
      years.push({ value: y, label: String(y) });
    }
    return years;
  };

  const getAvailableMonths = () => {
    const months = [
      { value: 1, label: "January" },
      { value: 2, label: "February" },
      { value: 3, label: "March" },
      { value: 4, label: "April" },
      { value: 5, label: "May" },
      { value: 6, label: "June" },
      { value: 7, label: "July" },
      { value: 8, label: "August" },
      { value: 9, label: "September" },
      { value: 10, label: "October" },
      { value: 11, label: "November" },
      { value: 12, label: "December" },
    ];

    if (Number(selectedMonthYear) === currentYear) {
      return months.filter((m) => m.value <= currentMonth);
    }
    if (Number(selectedMonthYear) > currentYear) {
      return [];
    }
    return months;
  };

  const quarterOptions = [
    { value: "Q1", label: "Q1" },
    { value: "Q2", label: "Q2" },
    { value: "Q3", label: "Q3" },
    { value: "Q4", label: "Q4" },
  ];

  const reset = () => {
    setSelectedYear(currentYear);
    setSelectedQuarter("");
    setSelectedMonthYear(currentYear);
    setSelectedMonth("");
  };

  return {
    // Quarter state
    selectedYear,
    setSelectedYear,
    selectedQuarter,
    setSelectedQuarter,
    // Month state
    selectedMonthYear,
    setSelectedMonthYear,
    selectedMonth,
    setSelectedMonth,
    // Options
    getQuarterYearOptions,
    getMonthYearOptions,
    getAvailableMonths,
    quarterOptions,
    // Range calculators
    getQuarterRange,
    getMonthRange,
    // Reset
    reset,
    // Current date info
    currentYear,
    currentMonth,
  };
};
